import {
  AlertTitle,
  CloseButton,
  Flex,
  Alert as ChakraAlert,
  Button,
  ButtonProps,
} from "@chakra-ui/react";
import { Info } from "@airhelp/icons";
import { AlertProps, alertThemes } from "./Alert";
import { useNavigation } from "../../hooks/useNavigation";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useLastEnquiry } from "../../model/claim-enquiry/useLastEnquiry";
import { useFetchClaimEnquiry } from "../../v2/modules/hooks/useFetchClaimEnquiry";
import { useUser } from "v2/modules/auth";

function ContinueClaimingButton({ uuid, ...props }: { uuid: string } & ButtonProps) {
  const { goTo } = useNavigation();
  const { t } = useTranslation();

  return (
    <Button
      size="s"
      w="100%"
      h="40px"
      border="none"
      variant="subtle"
      color="primary.500"
      data-testid="returning-user-continue"
      onClick={() => goTo(`/claims/verify/${uuid}`)}
      {...props}
    >
      {t("components.step_buttons.continue")}
    </Button>
  );
}

export function ReturningUserAlert({ ...props }: Omit<AlertProps, "isOpen">) {
  const { t } = useTranslation();
  const styles = alertThemes["action"];
  const { lastEnquiryUuid, clearLastEnquiryCookie } = useLastEnquiry();
  const { isAuthenticated } = useUser();
  const [isReturningUserAlertOpen, setReturningUserAlertState] = useState<boolean>(false);
  const { isError, isLoading } = useFetchClaimEnquiry({
    uuid: lastEnquiryUuid,
    enabled: !!lastEnquiryUuid && isAuthenticated,
  });

  useEffect(() => {
    if (!!lastEnquiryUuid && !isError && !isLoading && isAuthenticated) {
      setReturningUserAlertState(true);
    }
  }, [isError, isLoading, lastEnquiryUuid, isAuthenticated]);

  function onClose() {
    setReturningUserAlertState(false);
    clearLastEnquiryCookie();
  }

  if (!isReturningUserAlertOpen) return null;

  return (
    <ChakraAlert
      data-testid="returning-users-alert"
      borderBottom="1px"
      background={styles.background}
      borderColor={styles.borderColor}
      {...props}
    >
      <Flex
        width="100%"
        margin="0 auto"
        justifyContent="space-between"
        direction={{ base: "row", md: "row" }}
        flexWrap={{ base: "wrap", md: "nowrap" }}
        alignItems={{ base: "flex-start", md: "center" }}
      >
        <Flex w="100%" mx="auto" direction="row" alignItems={{ base: "flex-start", md: "center" }}>
          <Info
            mr={3}
            width="20px"
            height="20px"
            color={styles.iconColor}
            display={{ base: "none", md: "block" }}
          />
          <AlertTitle
            mr="auto"
            fontWeight="medium"
            mb={{ base: 4, md: 0 }}
            color={styles.textColor}
          >
            {t("alerts.returning_user.title")}
          </AlertTitle>
          <ContinueClaimingButton
            mr={2}
            flexShrink={0}
            w="auto"
            uuid={lastEnquiryUuid}
            display={{ base: "none", md: "block" }}
          />
          <CloseButton
            color={styles.closeButtonColor}
            aria-label="Close notification"
            data-testid="returning-users-close-mobile"
            onClick={onClose}
          />
        </Flex>
        <ContinueClaimingButton uuid={lastEnquiryUuid} display={{ base: "block", md: "none" }} />
      </Flex>
    </ChakraAlert>
  );
}
